import React, {useCallback, useState} from 'react';
import {useNavigate} from 'react-router';
import {Concept} from 'types';
import type {EditorMode} from 'editors/types';
import type {Agent, SaveAgentOptions} from 'services/agent/types';
import {useLocalization} from 'localization';
import type {DeleteAgentFunc, SaveAgentFunc} from 'services/agent';
import {FlexBox, Scene} from 'components';
import {EditAgent} from 'editors/agent/components/EditAgent';
import {EditAgentWizard} from 'editors/agent/components/EditAgentWizard';
import {useSceneExtensionsContext} from 'sceneExtensions';
import {AgentTitle} from './components';
import {
  useAgentTitle,
  useAgentUrlParameters,
  useNewOrExistingAgent,
} from './hooks';

export const AgentEditSceneCore: React.FC = () => {
  const {t} = useLocalization();
  const navigate = useNavigate();
  const params = useAgentUrlParameters();
  const [mode, setMode] = useState<EditorMode>(
    params.agentId ? 'edit' : 'register',
  );

  const {agent, agentType, suggestion, saveAgent, deleteAgent} =
    useNewOrExistingAgent(params);

  const title = useAgentTitle(mode, agent, agentType);
  const {currentExtension} = useSceneExtensionsContext();

  const handleCancel = useCallback(() => {
    if (params.agentId) {
      navigate(`/agent/${params.agentId}`);
    } else {
      navigate('/agent');
    }
  }, [navigate, params.agentId]);

  const handleSaveNew = useCallback<SaveAgentFunc>(
    (agent: Agent, options: SaveAgentOptions) => {
      return saveAgent(agent, options).then(agent => {
        // Allow snacks to show before navigating
        setTimeout(() => {
          navigate(`/agent/${agent.id}`);
        }, 500);
        return agent;
      });
    },
    [navigate, saveAgent],
  );

  const handleDelete = useCallback<DeleteAgentFunc>(
    (agent: Agent) => {
      return deleteAgent(agent).then(agent => {
        // Allow snacks to show before navigating
        setTimeout(() => {
          navigate('/agent');
        }, 500);
        return agent;
      });
    },
    [deleteAgent, navigate],
  );

  return (
    <Scene
      browserTitle={t('appbar.agents')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Content loadStatus={agent?.status} error={agent?.error?.message}>
        <AgentTitle title={title} agent={agent?.data} />
        <FlexBox>
          {agent && agent.status === 'Loaded' ? (
            // Edit existing agent
            agent.data.agentType === Concept.publisher ? (
              <EditAgent
                originalAgent={agent.data}
                onCancel={handleCancel}
                onSave={saveAgent}
                onDelete={handleDelete}
              />
            ) : (
              <EditAgentWizard
                originalAgent={agent.data}
                onModeChange={setMode}
                onCancel={handleCancel}
                onSave={saveAgent}
                onDelete={deleteAgent}
              />
            )
          ) : agentType ? (
            // Create new agent of given agentType
            agentType === Concept.publisher ? (
              <EditAgent
                agentType={agentType}
                suggestion={suggestion}
                onCancel={handleCancel}
                onSave={handleSaveNew}
              />
            ) : (
              <EditAgentWizard
                agentType={agentType}
                suggestion={suggestion}
                onModeChange={setMode}
                onCancel={handleCancel}
                onSave={handleSaveNew}
                onDelete={deleteAgent}
              />
            )
          ) : null}
        </FlexBox>
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
